/**
 * @file SidebarLogo.tsx
 * @module _admin/layout/components/sidebar/SidebarLogo
 * @description Renders the SidebarLogo layout component for the shared UTrade Admin sidebar.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { toAbsoluteUrl } from '../../../helpers';
import { useLayout } from '../../core';
import { getEnvironmentIndicatorClass } from '../../core/environmentIndicator';
// Media
import BurgurMenu from '../../../../_admin/assets/media/svg_uTrade/burgur-menu.svg';
import AdminLogo from '../../../../_admin/assets/media/admin.png';
import FulFillmentLogo from '../../../../_admin/assets/media/fulfillment.png';
import LoadingBayLogo from '../../../../_admin/assets/media/loadingBay.png';
import CustomerServiceLogo from '../../../../_admin/assets/media/customerService.png';
import FinanceLogo from '../../../../_admin/assets/media/finance.png';
import { useAuth } from '../../../../app/modules/auth';
import {
  ActivationManager,
  ActivationRep,
  FinanceManager,
  FulfillmentManager,
  LoadingBayManager,
  Manager,
  ServiceMember,
} from '../../../../utils/constants';
import ImageWithFallback from '../../../../app/modules/_common/components/image-with-fallback';
const SidebarLogo = () => {
  const { config } = useLayout();
  const { currentUser } = useAuth();
  const appSidebarDefaultMinimizeDesktopEnabled =
    config?.app?.sidebar?.default?.minimize?.desktop?.enabled;
  const appSidebarDefaultCollapseDesktopEnabled =
    config?.app?.sidebar?.default?.collapse?.desktop?.enabled;
  const toggleType = appSidebarDefaultCollapseDesktopEnabled
    ? 'collapse'
    : appSidebarDefaultMinimizeDesktopEnabled
    ? 'minimize'
    : '';
  const toggleState = appSidebarDefaultMinimizeDesktopEnabled ? 'active' : '';
  const appSidebarDefaultMinimizeDefault =
    config.app?.sidebar?.default?.minimize?.desktop?.default;
  const userRole = currentUser?.role;
  let logoSource = AdminLogo;
  if (userRole) {
    if (userRole.some((obj: any) => obj.type === LoadingBayManager)) {
      logoSource = LoadingBayLogo;
    } else if (userRole.some((obj: any) => obj.type === FulfillmentManager)) {
      logoSource = FulFillmentLogo;
    } else if (userRole.some((obj: any) => obj.type === FinanceManager)) {
      logoSource = FinanceLogo;
    } else if (
      userRole.some(
        (obj: any) => obj.type === ServiceMember || obj.type === Manager || obj.type === ActivationManager || obj.type === ActivationRep
      )
    ) {
      logoSource = CustomerServiceLogo;
    }
  }
  let link = '/dashboard';
  if (userRole) {
    if (userRole.some((obj: any) => obj.type === LoadingBayManager)) {
      link = '/orders';
    } else if (userRole.some((obj: any) => obj.type === FulfillmentManager)) {
      link = '/sellers/list-seller';
    } else if (userRole.some((obj: any) => obj.type === FinanceManager)) {
      link = '/dashboard';
    } else if (userRole.some((obj: any) => obj.type === Manager || obj.type === ActivationManager)) {
      link = '/sales-management/manager/dashboard';
    } else if (userRole.some((obj: any) => obj.type === ServiceMember || obj.type === ActivationRep)) {
      link = '/sales-management/representative/dashboard';
    }
  }

  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV;
  return (
    <div
      className={clsx(
        'app-sidebar-logo px-6',
        getEnvironmentIndicatorClass(environment)
      )}
      id="kt_app_sidebar_logo"
    >
      <Link to={link}>
        {config.layoutType === 'dark-sidebar' ? (
          <img
            alt="Logo"
            src={logoSource}
            className="h-60px app-sidebar-logo-default"
          />
        ) : (
          <>
            <ImageWithFallback
              alt="Logo"
              src={toAbsoluteUrl('/media/logos/default.svg')}
              className="h-25px app-sidebar-logo-default theme-light-show"
              noDefaultSize
            />
            <ImageWithFallback
              alt="Logo"
              src={toAbsoluteUrl('/media/logos/default-dark.svg')}
              className="h-25px app-sidebar-logo-default theme-dark-show"
              noDefaultSize
            />
          </>
        )}
      </Link>
      {(appSidebarDefaultMinimizeDesktopEnabled ||
        appSidebarDefaultCollapseDesktopEnabled) && (
        <div
          id="kt_app_sidebar_toggle"
          className={clsx(
            'app-sidebar-toggle btn btn-flush bg-transprant rotate',
            { active: appSidebarDefaultMinimizeDefault }
          )}
          data-kt-toggle="true"
          data-kt-toggle-state={toggleState}
          data-kt-toggle-target="body"
          data-kt-toggle-name={`app-sidebar-${toggleType}`}
        >
          <img
            src={BurgurMenu}
            className="h-34px w-34px rotate-180 d-none d-lg-flex"
            alt=""
          />
        </div>
      )}
    </div>
  );
};
export { SidebarLogo };
